import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MatchAction, MatchCard } from "@hub/shared";
import { toast } from "@/ui";
import { api, invokeEngine, useSession } from "../_shims";
import { costsCredits } from "../lib/cards";
import { MATCHES_KEY } from "../lib/hooks";

/**
 * Shared list behaviour: clicking a row opens the detail modal; a row action runs at once when it is free and needs no
 * note, otherwise the modal opens with that action preselected so the note or the credit confirmation is shown.
 */
export function useMatchOpener() {
  const qc = useQueryClient();
  const { isSignedIn } = useSession();
  const [modal, setModal] = useState<{ card: MatchCard; action: MatchAction | null } | null>(null);
  const quick = useMutation({
    mutationFn: ({ card, action }: { card: MatchCard; action: MatchAction }) => api.matchAction(card.matchId, { action: action.id }),
    onSuccess: async (res, { action }) => {
      await invokeEngine("cacheMatches", { cards: [res.card] });
      qc.invalidateQueries({ queryKey: MATCHES_KEY });
      qc.invalidateQueries({ queryKey: ["engine", "getMatchCards"] });
      if (res.revealed || res.conversationId) setModal({ card: res.card, action: null });
      toast(`${action.label}: done.`, "success");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "The action failed.", "danger"),
  });

  const open = useCallback((card: MatchCard) => setModal({ card, action: null }), []);
  const act = useCallback(
    (card: MatchCard, action: MatchAction) => {
      if (action.disabled) return;
      if (!isSignedIn) {
        toast("Sign in to act on introductions.", "neutral");
        return;
      }
      // Dismiss opens the dialog too, for its optional "why" (the owner's Feedback tab counts the reasons).
      if (action.requiresNote || costsCredits(action) || action.id === "mark_complete" || action.id === "accept_match" || action.id === "dismiss") {
        setModal({ card, action });
        return;
      }
      quick.mutate({ card, action });
    },
    [isSignedIn, quick],
  );
  const close = useCallback(() => setModal(null), []);
  return { modal, open, act, close, busy: quick.isPending };
}
